// pages/qrcode/QRCodeButton.js
import React, { useState, useEffect, useContext } from "react"
import { useNavigate } from "react-router-dom"
import api from "../../services/api"
import { UserContext } from "../../context/UserContext"
import { useTranslation } from "react-i18next"

function QRCodeButton() {
  console.log("[QRCODEBUTTON] Rendering")
  const { t } = useTranslation()
  const { userData } = useContext(UserContext)
  const navigate = useNavigate()
  const [barcodeName, setBarcodeName] = useState("")
  const [qrData, setQrData] = useState("")
  const [publicUrl, setPublicUrl] = useState("")
  const [barcodes, setBarcodes] = useState([])
  const [sites, setSites] = useState([])
  const [selectedSiteId, setSelectedSiteId] = useState("")
  const [questionnaires, setQuestionnaires] = useState([])
  const [selectedQuestionnaireId, setSelectedQuestionnaireId] = useState("")
  const [emails, setEmails] = useState([])
  const [emailInput, setEmailInput] = useState("")

  useEffect(() => {
    console.log("[QRCODEBUTTON] useEffect start")
    loadBarcodes()
    loadSites()
    loadQuestionnaires()
  }, [])

  const loadBarcodes = async () => {
    console.log("[QRCODEBUTTON] loadBarcodes")
    try {
      const res = await api.get("/barcode/list")
      setBarcodes(res.data.barcodes)
    } catch (err) {
      console.log("[QRCODEBUTTON] loadBarcodes error", err)
    }
  }

  const loadSites = async () => {
    console.log("[QRCODEBUTTON] loadSites")
    try {
      const res = await api.get("/site/all")
      setSites(res.data.sites || [])
    } catch (err) {
      console.log("[QRCODEBUTTON] loadSites error", err)
    }
  }

  const loadQuestionnaires = async () => {
    console.log("[QRCODEBUTTON] loadQuestionnaires")
    try {
      const res = await api.get("/questionnaires/list")
      setQuestionnaires(res.data.questionnaires || [])
    } catch (err) {
      console.log("[QRCODEBUTTON] loadQuestionnaires error", err)
    }
  }

  const handleAddEmail = () => {
    console.log("[QRCODEBUTTON] handleAddEmail")
    if (emailInput.trim()) {
      setEmails([...emails, emailInput.trim()])
      setEmailInput("")
    }
  }

  const handleCreateBarcode = async () => {
    console.log("[QRCODEBUTTON] handleCreateBarcode")
    if (!barcodeName || !selectedSiteId) {
      alert(t("qr_code_button.errors.missing_fields"))
      return
    }
    try {
      const body = {
        name: barcodeName,
        siteId: selectedSiteId,
        receivers: emails
      }
      if (selectedQuestionnaireId) {
        body.questionnaireId = selectedQuestionnaireId
      }
      const res = await api.post("/barcode/create", body)
      setQrData(res.data.dataURL)
      setPublicUrl(res.data.publicURL)
      setBarcodeName("")
      setSelectedSiteId("")
      setSelectedQuestionnaireId("")
      setEmails([])
      loadBarcodes()
    } catch (err) {
      console.log("[QRCODEBUTTON] handleCreateBarcode error", err)
      alert(t("qr_code_button.errors.creation_failed"))
    }
  }

  const handleViewBarcode = (b) => {
    console.log("[QRCODEBUTTON] handleViewBarcode")
    const newWin = window.open("", "_blank")
    if (!newWin) return
    newWin.document.write(`<!DOCTYPE html><html><head><title>QR Code</title></head>
      <body>
        <img src="${b.qrCodeData}" style="width:600px;" />
        <div>
          <a href="${b.qrCodeData}" download="barcode-${b.barcodeId}.png">${t("qr_code_button.buttons.download")}</a>
          <button onclick="window.print()">${t("qr_code_button.buttons.print")}</button>
        </div>
      </body>
      </html>
    `)
    newWin.document.close()
  }

  const handleDeleteBarcode = async (id) => {
    console.log("[QRCODEBUTTON] handleDeleteBarcode")
    try {
      await api.delete(`/barcode/delete/${id}`)
      loadBarcodes()
    } catch (err) {
      console.log("[QRCODEBUTTON] handleDeleteBarcode error", err)
      alert(t("qr_code_button.errors.delete_failed"))
    }
  }

  const handleEditBarcode = (id) => {
    console.log("[QRCODEBUTTON] handleEditBarcode")
    navigate(`/app/barcode-edit/${id}`)
  }

  return (
    <div>
      <h2>{t("qr_code_button.title")}</h2>
      <input
        type="text"
        placeholder={t("qr_code_button.placeholders.barcode_name")}
        value={barcodeName}
        onChange={(e) => setBarcodeName(e.target.value)}
      />
      <select value={selectedSiteId} onChange={(e) => setSelectedSiteId(e.target.value)}>
        <option value="">{t("qr_code_button.placeholders.select_site")}</option>
        {sites.map((site) => (
          <option key={site._id} value={site._id}>
            {site.plainName} - {site.address}
          </option>
        ))}
      </select>
      <select value={selectedQuestionnaireId} onChange={(e) => setSelectedQuestionnaireId(e.target.value)}>
        <option value="">Select Questionnaire (Optional)</option>
        {questionnaires.map((q) => (
          <option key={q._id} value={q._id}>
            {q.name}
          </option>
        ))}
      </select>

      <div style={{ marginTop: "10px" }}>
        <input
          type="email"
          placeholder="Enter receiver's email"
          value={emailInput}
          onChange={(e) => setEmailInput(e.target.value)}
        />
        <button onClick={handleAddEmail} style={{ marginLeft: "5px" }}>
          +
        </button>
        <div>
          {emails.map((email, index) => (
            <div key={index}>{email}</div>
          ))}
        </div>
      </div>

      <button onClick={handleCreateBarcode} style={{ marginTop: "10px" }}>
        {t("qr_code_button.buttons.create")}
      </button>

      {qrData && (
        <div>
          <h4>{t("qr_code_button.generated_qr")}</h4>
          <img src={qrData} alt="QR Code" style={{ width: 200 }} />
          <p>{t("qr_code_button.public_url")}: {publicUrl}</p>
        </div>
      )}

      <h4>{t("qr_code_button.my_barcodes")}</h4>
      {barcodes.map((b) => (
        <div key={b._id}>
          <p>{t("qr_code_button.labels.name")}: {b.name}</p>
          <p>{t("qr_code_button.labels.url")}: {`${process.env.REACT_APP_FRONTEND_URL}register/registration?barcodeId=${b.barcodeId}`}</p>
          {b.site && (
            <p>
              {t("qr_code_button.labels.linked_site")}: {b.site.plainName} - {b.site.address}
            </p>
          )}
          {b.receivers && b.receivers.length > 0 && (
            <p>
              Receivers: {b.receivers.join(", ")}
            </p>
          )}
          <button onClick={() => handleViewBarcode(b)}>{t("qr_code_button.buttons.view")}</button>
          <button onClick={() => handleDeleteBarcode(b._id)}>{t("qr_code_button.buttons.delete")}</button>
          <button onClick={() => handleEditBarcode(b._id)}>Edit</button>
          <hr />
        </div>
      ))}
    </div>
  )
}

export default QRCodeButton
